/**
 * North Star Decision Intelligence OS
 * TypeScript Type Definitions
 * 
 * Version: 2.0.0-phase2c-2
 * BIF MILESTONE: Changed PoolMode 'transactional' -> 'pick'
 * Phase 2C-2: Added Event, Prediction, Portfolio types
 */

// =============================================================================
// Core Enums (LOWERCASE ONLY)
// =============================================================================

export type PoolMode = 'gm' | 'pick';
export type PoolStatus = 'pending' | 'active' | 'locked' | 'completed' | 'cancelled';
export type PoolDomain = 'nfl' | 'nba' | 'college' | 'mlb' | 'nhl' | 'soccer' | 'corporate' | 'financial';

// =============================================================================
// Pool Types
// =============================================================================

export interface Pool {
  id: number;
  pool_id: string;
  pool_name: string;
  domain: PoolDomain;
  mode: PoolMode;
  status: PoolStatus;
  initial_bankroll: number;
  max_participants?: number;
  participant_count?: number;
  start_date: string;
  end_date: string;
  lock_time?: string;
  created_by?: string;
  description?: string;
  rules?: string;
  min_confidence?: number;
  max_predictions_per_portfolio?: number;
  created_at?: string;
  updated_at?: string;
}

// =============================================================================
// Membership Types
// =============================================================================

export interface PoolMembership {
  id: number;
  pool_id: string;
  user_id: string;
  current_balance: number;
  initial_balance?: number;
  predictions_made: number;
  predictions_won?: number;
  predictions_lost?: number;
  predictions_correct?: number;
  predictions_pending?: number;
  current_win_streak?: number;
  longest_win_streak?: number;
  longest_loss_streak?: number;
  is_active?: boolean;
  is_depleted: boolean;
  joined_at: string;
  // BIF Milestone: Behavioral metrics
  total_stake_events?: number;
  avg_stake_percentage?: number;
  avg_decision_time_seconds?: number;
  last_decision_time_seconds?: number;
  loss_chasing_counter?: number;
  
  // Backend snake_case fields (used by PoolDetails.tsx)
  starting_balance?: number;
  correct_predictions?: number;
  incorrect_predictions?: number;
  total_won?: number;
  total_lost?: number;
}

// =============================================================================
// Leaderboard Types
// =============================================================================

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  current_balance: number;
  predictions_made: number;
  predictions_won?: number;
  predictions_correct?: number;
  win_rate: number;
  is_depleted: boolean;
}

// =============================================================================
// BSL Types
// =============================================================================

export interface BSLProfile {
  user_id?: string;
  pool_id?: string;
  exposure?: {
    units_at_risk: number;
    exposure_percentage: number;
    current_balance: number;
    total_staked: number;
  };
  streaks?: {
    current_streak: number;
    longest_win_streak: number;
    longest_loss_streak: number;
  };
  confidence?: {
    user_confidence: number;
    historical_accuracy: number;
    drift_difference: number;
  };
  activity?: {
    predictions_made: number;
    predictions_correct: number;
    predictions_pending: number;
    last_prediction_at?: string;
  };
  // Phase 2C-2 additions
  stimulus_score?: number;
  behavioral_metrics?: {
    decision_frequency: number;
    stake_variance: number;
    outcome_sensitivity: number;
  };
  last_updated?: string;
}

// =============================================================================
// Transaction Types
// =============================================================================

export type TransactionType = 'INITIAL_BALANCE' | 'STAKE' | 'WIN' | 'LOSS' | 'ADMIN_ADJUSTMENT' | 'REFUND';

export interface BankrollTransaction {
  id: number;
  membership_id: number;
  transaction_type: TransactionType;
  amount: number;
  balance_before: number;
  balance_after: number;
  description?: string;
  prediction_id?: string;
  created_at: string;
}

// =============================================================================
// Composite Types
// =============================================================================

export interface PoolWithMembership {
  pool: Pool;
  membership: PoolMembership;
}

// =============================================================================
// Input Types
// =============================================================================

export interface CreatePoolInput {
  pool_id: string;
  pool_name: string;
  domain: PoolDomain;
  mode: PoolMode;
  initial_bankroll: number;
  max_participants: number;
  start_date: string;
  end_date: string;
  lock_time: string;
  description?: string;
}

export interface UpdatePoolInput {
  pool_name?: string;
  description?: string;
  rules?: string;
}

export interface BalanceAdjustmentInput {
  amount: number;
  reason: string;
}

export interface PoolFilters {
  domain?: PoolDomain;
  status?: PoolStatus;
  mode?: PoolMode;
}

// =============================================================================
// Admin & Auth
// =============================================================================

// DEPRECATED: Admin status determined by database (Phase 3H-A)

export function isAdminUser(_userId: string): boolean {
  console.warn('isAdminUser() deprecated'); return false;
}

// =============================================================================
// App Configuration
// =============================================================================

export interface AppConfig {
  apiBaseUrl: string;
  mockAuth: boolean;
  disablePolling: boolean;
}

export function getAppConfig(): AppConfig {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api/v1',
    mockAuth: import.meta.env.VITE_MOCK_AUTH === 'true',
    disablePolling: import.meta.env.VITE_DISABLE_POLLING === 'true',
  };
}

// =============================================================================
// BIF MILESTONE: Decision Event Types
// =============================================================================

export type DecisionEventType = 
  | 'stake_placed'
  | 'portfolio_submitted'
  | 'prediction_settled'
  | 'session_start'
  | 'session_end'
  | 'balance_checked'
  | 'leaderboard_viewed'
  | 'loss_chase_detected';

export interface DecisionEvent {
  id: string;  // UUID
  user_id: string;
  pool_id: number;
  mode: PoolMode;
  event_type: DecisionEventType;
  timestamp: string;
  decision_time_seconds?: number;
  time_to_lock_seconds?: number;
  stake_amount?: number;
  stake_percentage?: number;
  prediction_id?: string;
  portfolio_id?: string;
  event_metadata?: Record<string, unknown>;
}

// =============================================================================
// BIF MILESTONE: Analytics Models (Admin-only)
// =============================================================================

export interface DecisionTimeCurve {
  bins: number[];      // Time buckets in seconds
  values: number[];    // Count of decisions in each bucket
}

export interface StakeIntensityDistribution {
  buckets: string[];   // e.g., "0-10%", "10-25%", etc.
  counts: number[];    // Count in each bucket
}

export interface LossChasingIndex {
  user_id: string;
  pool_id: string;
  index_value: number;       // 0.0 to 1.0 scale
  chase_events: number;      // Count of detected chase events
  recovery_rate: number;     // % of chases that resulted in recovery
}

export interface BehavioralAnalyticsSummary {
  pool_id: string;
  total_events: number;
  unique_users: number;
  avg_decision_time: number;
  decision_time_curve: DecisionTimeCurve;
  stake_distribution: StakeIntensityDistribution;
  loss_chasing_prevalence: number;  // % of users with detected chasing
}

// =============================================================================
// Phase 2C-2: Event Types
// =============================================================================

export interface Event {
  event_id: string;
  pool_id: string;
  title?: string;
  description?: string;
  domain: string;
  event_time?: string;
  lock_time?: string;
  status: 'open' | 'upcoming' | 'locked' | 'settled' | 'completed' | 'cancelled';
  prediction_type?: string;
  home_team?: string;
  away_team?: string;
  outcomes?: EventOutcome[];
  actual_outcome?: string;
  created_at: string;
}

export interface EventOutcome {
  outcome_id?: string;
  label: string;
  odds?: number;
  description?: string;
}

// =============================================================================
// Phase 2C-2: Prediction Types
// =============================================================================

export type PredictionStatus = 'pending' | 'won' | 'lost' | 'cancelled';

export interface Prediction {
  prediction_id: string;
  pool_id: string;
  user_id: string;
  portfolio_id?: string;
  event_id: string;
  prediction_type?: string;
  predicted_outcome: string;
  confidence?: number;
  wager_amount: number;
  potential_payout?: number;
  status: PredictionStatus;
  actual_outcome?: string;
  settled_at?: string;
  created_at: string;
}

export interface CreatePredictionRequest {
  pool_id: string;
  user_id?: string;
  event_id: string;
  prediction_type?: string;
  predicted_outcome: string;
  confidence?: number;
  wager_amount: number;
}

// =============================================================================
// Phase 2C-2: Portfolio Types (GM Mode)
// =============================================================================

export type PortfolioStatus = 'pending' | 'active' | 'settled' | 'completed' | 'cancelled';

export interface Portfolio {
  portfolio_id: string;
  pool_id: string;
  user_id: string;
  status: PortfolioStatus;
  total_stake?: number;
  allocations?: PortfolioAllocation[];
  created_at: string;
  settled_at?: string;
}

export interface PortfolioAllocation {
  event_id: string;
  predicted_outcome: string;
  allocation_percentage: number;
  confidence?: number;
}

export interface CreatePortfolioRequest {
  pool_id: string;
  user_id?: string;
  allocations: PortfolioAllocation[];
}

// =============================================================================
// Phase 2C-2: API Response Types
// =============================================================================

export interface LeaderboardResponse {
  pool_id: string;
  entries: LeaderboardEntry[];
  updated_at: string;
}

export interface JoinPoolRequest {
  user_id: string;
}

export interface CreatePoolRequest {
  pool_id?: string;
  pool_name: string;
  domain: PoolDomain;
  mode: PoolMode;
  start_date: string;
  end_date: string;
  lock_time?: string;
  initial_bankroll: number;
  max_participants?: number;
  min_confidence?: number;
  description?: string;
  rules?: string;
}
// =============================================================================
// Phase 2C-3: Admin Types (ADD to existing types.ts)
// =============================================================================

// Admin Stats
export interface AdminStats {
  total_pools: number;
  active_pools: number;
  pending_pools: number;
  locked_pools: number;
  completed_pools: number;
  total_memberships: number;
  total_users: number;
  recent_activity_count: number;
}

// Admin Pool Response (with admin-specific fields)
export interface AdminPool {
  pool_id: string;
  name: string;
  domain: string;
  mode: string;
  status: string;
  member_count: number;
  total_bankroll: number;
  created_at: string;
}

export interface AdminPoolListResponse {
  pools: AdminPool[];
  total: number;
  limit: number;
  offset: number;
}

// Admin Membership Response
export interface AdminMembership {
  id: number;
  pool_id: string;
  pool_name: string;
  user_id: string;
  current_balance: number;
  initial_balance: number;
  predictions_made: number;
  predictions_won: number;
  predictions_lost: number;
  is_depleted: boolean;
  joined_at: string;
}

export interface AdminMembershipListResponse {
  memberships: AdminMembership[];
  total: number;
  limit: number;
  offset: number;
}

// Balance Adjustment
export interface AdjustBalanceRequest {
  amount: number;
  reason: string;
}

export interface AdjustBalanceResponse {
  membership_id: number;
  user_id: string;
  pool_id: string;
  previous_balance: number;
  new_balance: number;
  adjustment: number;
  reason: string;
}

// Remove Member
export interface RemoveMemberRequest {
  reason: string;
}

export interface RemoveMemberResponse {
  user_id: string;
  pool_id: string;
  final_balance: number;
  removed_at: string;
  reason: string;
}

// Audit Log
export interface AuditLogEntry {
  id: number;
  admin_user_id: string;
  action: string;
  target_type: string;
  target_id: string;
  old_value?: Record<string, unknown>;
  new_value?: Record<string, unknown>;
  reason?: string;
  ip_address?: string;
  created_at: string;
}

export interface AuditLogListResponse {
  entries: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AuditLogFilters {
  admin_user_id?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  start_date?: string;
  end_date?: string;
}

// Create Pool Request (for admin)
export interface AdminCreatePoolRequest {
  pool_id?: string;
  pool_name: string;
  domain: PoolDomain;
  mode: PoolMode;
  initial_bankroll: number;
  max_participants?: number;
  start_date: string;
  end_date: string;
  lock_time?: string;
  description?: string;
  rules?: string;
  min_confidence?: number;
}

// Update Pool Request (for admin)
export interface AdminUpdatePoolRequest {
  pool_name?: string;
  description?: string;
  rules?: string;
  end_date?: string;
}

// Lock Pool Request
export interface LockPoolRequest {
  reason?: string;
}
// =============================================================================
// Phase 2C-5: GM Roster Types
// =============================================================================

export type GMRosterPosition = 'QB' | 'RB1' | 'RB2' | 'WR1' | 'WR2' | 'TE' | 'K';

export const GM_ROSTER_POSITIONS: GMRosterPosition[] = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'K'];

export interface GMRosterSlotData {
  position: GMRosterPosition;
  event_id: string | null;
  predicted_outcome: string | null;
  allocation_percentage: number;
}

export interface GMRosterState {
  slots: GMRosterSlotData[];
  totalAllocation: number;
  isValid: boolean;
}

// =============================================================================
// Phase 2D-2: Pick Mode Types (Real Markets/Outcomes)
// =============================================================================

export interface MarketOutcome {
  outcome_id: number;
  name: string;
  value: string | null;
  odds: number;
}

export interface Market {
  market_id: number;
  market_type: 'spread' | 'moneyline' | 'total';
  market_name: string;
  line_value: number | null;
  outcomes: MarketOutcome[];
}

export interface PickSlateEvent {
  event_id: string;
  event_name: string;
  home_team: string;
  away_team: string;
  event_date: string;
  lock_time: string;
  is_locked: boolean;
  markets: Market[];
}

export interface PickSlateResponse {
  pool_id: string;
  events: PickSlateEvent[];
  generated_at: string;
}

export interface SubmitPickRequest {
  event_id: string;
  market_id: number;
  outcome_id: number;
  stake_amount: number;
}

export interface PickResponse {
  prediction_id: string;
  pool_id: string;
  user_id: string;
  event_id: string;
  market_id: number;
  outcome_id: number;
  outcome_name: string;
  stake_amount: number;
  odds_at_submission: number;
  line_value_at_submission: number | null;
  status: 'pending' | 'won' | 'lost' | 'push' | 'cancelled';
  submitted_at: string;
}

// =============================================================================
// Phase 2D-2: GM Mode Types (Real Player Props)
// =============================================================================

export interface PlayerProp {
  prop_id: number;
  event_id: string;
  event_name: string;
  player_name: string;
  team: string;
  position: string | null;
  stat_type: string;
  line_value: number;
  over_odds: number;
  under_odds: number;
}

export interface GMSlateResponse {
  pool_id: string;
  player_props: PlayerProp[];
  positions: string[];
  generated_at: string;
}

export interface GMAllocationRequest {
  slot_position: GMRosterPosition;
  player_prop_id: number;
  direction: 'over' | 'under';
  allocation_percentage: number;
}

export interface SubmitPortfolioRequest {
  allocations: GMAllocationRequest[];
}

export interface GMAllocationResponse {
  slot_position: string;
  slot_index: number;
  player_prop_id: number;
  player_name: string;
  stat_type: string;
  line_value: number;
  direction: string;
  odds_at_submission: number;
  allocation_percentage: number;
}

export interface PortfolioResponse {
  portfolio_id: string;
  pool_id: string;
  user_id: string;
  allocations: GMAllocationResponse[];
  total_allocation: number;
  status: 'pending' | 'resolved';
  submitted_at: string;
  is_locked: boolean;
}